/**
 * Third-party widgets attach as body-level nodes (or inside app-embed
 * `shopify-section`s) with arbitrary z-indexes. Theme dialogs make those nodes
 * `inert`, which is required for WCAG 2.4.3, but `inert` does not hide them.
 *
 * `visibility: hidden` on a host is not enough: widgets commonly set
 * `visibility: visible` on an inner iframe, which re-paints the overlay on top
 * of the dialog. `display: none` cannot be overridden by descendants.
 *
 * While any modal or the header menu is open, hide every node whose z-index is
 * higher than every theme modal. Restore on close. Do not maintain a vendor
 * allowlist.
 */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE']);
const FALLBACK_MODAL_Z_INDEX = 102;

let pausedOverlays = [];
let injectObserver = null;
let injectFrame = null;

const consentSelector = () => {
  if (typeof PopupModal !== 'undefined' && PopupModal.CONSENT_SURFACE_SELECTOR) {
    return PopupModal.CONSENT_SURFACE_SELECTOR;
  }

  return '[id^="shopify-pc__"], [class*="shopify-pc__"]';
};

const parseZIndex = (value) => {
  const zIndex = Number.parseInt(String(value).trim(), 10);

  return Number.isFinite(zIndex) ? zIndex : null;
};

const isBlockingUiOpen = () => {
  return typeof PopupModal !== 'undefined' && PopupModal.isBlockingUiOpen();
};

const isPopupModalElement = (element) => {
  return typeof PopupModal !== 'undefined' && element instanceof PopupModal;
};

const getBlockingSurfaces = () => {
  const surfaces = [];

  for (const element of document.querySelectorAll('[open="true"]')) {
    if (element instanceof HTMLElement && isPopupModalElement(element)) {
      surfaces.push(element);
    }
  }

  const menu = document.querySelector('.header-menu[data-menu-state="open"]');

  if (menu instanceof HTMLElement) {
    surfaces.push(menu);
  }

  return surfaces;
};

const getMaxModalZIndex = () => {
  const fromToken = parseZIndex(
    getComputedStyle(document.documentElement).getPropertyValue('--z-popup')
  );
  let maxZIndex = fromToken ?? FALLBACK_MODAL_Z_INDEX;

  for (const element of document.querySelectorAll('[role="dialog"]')) {
    if (!(element instanceof HTMLElement) || !isPopupModalElement(element)) {
      continue;
    }

    const zIndex = parseZIndex(getComputedStyle(element).zIndex);

    if (zIndex != null) {
      maxZIndex = Math.max(maxZIndex, zIndex);
    }
  }

  return maxZIndex;
};

const isOnBlockingSurface = (element, surfaces) => {
  return surfaces.some((surface) => {
    return element === surface || element.contains(surface) || surface.contains(element);
  });
};

const isConsentSurface = (element) => {
  const selector = consentSelector();

  return element.matches(selector) || Boolean(element.closest(selector));
};

const isExempt = (element, surfaces) => {
  if (SKIP_TAGS.has(element.tagName) || element.matches('.skip-to-content')) {
    return true;
  }

  return isOnBlockingSurface(element, surfaces) || isConsentSurface(element);
};

const stacksAboveModals = (element, maxModalZIndex) => {
  const style = getComputedStyle(element);

  if (style.display === 'none') {
    return false;
  }

  const zIndex = parseZIndex(style.zIndex);

  return zIndex != null && zIndex > maxModalZIndex;
};

const pushChildren = (stack, node) => {
  if (node instanceof HTMLElement && node.shadowRoot) {
    stack.push(...node.shadowRoot.children);
  }

  if (node instanceof Element || node instanceof DocumentFragment) {
    stack.push(...node.children);
  }
};

const subtreeStacksAboveModals = (root, maxModalZIndex, surfaces) => {
  const stack = [root];

  while (stack.length > 0) {
    const node = stack.pop();

    if (node instanceof HTMLElement && !isExempt(node, surfaces) && stacksAboveModals(node, maxModalZIndex)) {
      return true;
    }

    pushChildren(stack, node);
  }

  return false;
};

const collectHighZDescendants = (root, surfaces, maxModalZIndex, overlays) => {
  const stack = [];

  pushChildren(stack, root);

  while (stack.length > 0) {
    const node = stack.pop();

    if (node instanceof HTMLElement && !isExempt(node, surfaces) && stacksAboveModals(node, maxModalZIndex)) {
      overlays.add(node);
      continue;
    }

    pushChildren(stack, node);
  }
};

const collectOverlays = (surfaces, maxModalZIndex) => {
  const overlays = new Set();

  if (!document.body) {
    return overlays;
  }

  const considerHost = (host) => {
    if (!(host instanceof HTMLElement) || SKIP_TAGS.has(host.tagName) || isConsentSurface(host)) {
      return;
    }

    if (isOnBlockingSurface(host, surfaces) || host.matches('main, .skip-to-content')) {
      collectHighZDescendants(host, surfaces, maxModalZIndex, overlays);
      return;
    }

    if (subtreeStacksAboveModals(host, maxModalZIndex, surfaces)) {
      overlays.add(host);
    }
  };

  for (const child of document.documentElement.children) {
    if (child === document.body || child.tagName === 'HEAD') {
      continue;
    }

    considerHost(child);
  }

  for (const child of document.body.children) {
    considerHost(child);
  }

  return overlays;
};

const pauseOverlay = (element) => {
  if (pausedOverlays.some((entry) => entry.element === element || entry.element.contains(element))) {
    return;
  }

  pausedOverlays = pausedOverlays.filter((entry) => !element.contains(entry.element));

  pausedOverlays.push({
    element,
    display: element.style.getPropertyValue('display'),
    displayPriority: element.style.getPropertyPriority('display'),
    inert: element.inert,
  });

  element.style.setProperty('display', 'none', 'important');
  element.inert = true;
};

const pauseOverlays = () => {
  const surfaces = getBlockingSurfaces();

  if (surfaces.length === 0) {
    return;
  }

  const maxModalZIndex = getMaxModalZIndex();

  for (const element of collectOverlays(surfaces, maxModalZIndex)) {
    pauseOverlay(element);
  }

  startInjectObserver();
};

const resumeOverlays = () => {
  stopInjectObserver();

  for (const { element, display, displayPriority, inert } of pausedOverlays) {
    if (!element.isConnected) {
      continue;
    }

    if (display) {
      element.style.setProperty('display', display, displayPriority);
    } else {
      element.style.removeProperty('display');
    }

    element.inert = inert;
  }

  pausedOverlays = [];
};

const onInject = () => {
  if (!isBlockingUiOpen()) {
    return;
  }

  if (injectFrame !== null) {
    return;
  }

  injectFrame = window.requestAnimationFrame(() => {
    injectFrame = null;
    pauseOverlays();
  });
};

const startInjectObserver = () => {
  if (injectObserver || !document.body) {
    return;
  }

  injectObserver = new MutationObserver(onInject);
  injectObserver.observe(document.body, { childList: true, subtree: true });
  injectObserver.observe(document.documentElement, { childList: true });
};

const stopInjectObserver = () => {
  if (injectFrame !== null) {
    window.cancelAnimationFrame(injectFrame);
    injectFrame = null;
  }

  injectObserver?.disconnect();
  injectObserver = null;
};

const syncOverlays = () => {
  if (isBlockingUiOpen()) {
    pauseOverlays();
  } else {
    resumeOverlays();
  }
};

document.addEventListener('modal-stack:change', syncOverlays);

const headerMenu = document.querySelector('.header-menu');

if (headerMenu) {
  new MutationObserver(syncOverlays).observe(headerMenu, {
    attributes: true,
    attributeFilter: ['data-menu-state'],
  });
}

syncOverlays();
